import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import './AdminAgents.css';

const MODEL_GROUPS = [
  {
    label: 'Newer (recommended)',
    options: [
      { value: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash — best current workhorse for agents' },
      { value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash' },
      { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
      { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite — fastest / cheapest 3.5' },
      { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash-Lite — high-volume, low cost' }
    ]
  },
  {
    label: 'Current',
    options: [
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro — deep reasoning' },
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' }
    ]
  },
  {
    label: 'Legacy',
    options: [
      { value: 'gemini-2.0-flash-001', label: 'Gemini 2.0 Flash' }
    ]
  }
];

const MODEL_VALUES = MODEL_GROUPS.flatMap((group) => group.options.map((option) => option.value));

// Meal types that make sense to rule out of discovery. Mains are omitted
// deliberately — excluding them would leave nothing to find.
const EXCLUDABLE_CATEGORIES = ['dessert', 'drink', 'snack', 'side', 'appetizer', 'breakfast', 'brunch'];

const DEFAULT_BRIEF_PLACEHOLDER = 'Describe what discovery should search for, e.g. complete '
  + 'main-dish recipes a household would cook as the centrepiece of a meal, and what to avoid.';

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const apiBase = () => `${process.env.REACT_APP_API_URL}/admin/agents`;

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');

const AgentConfigCard = ({
  title,
  stepLabel,
  description,
  outcomeNote,
  configKey,
  config,
  status,
  onChange,
  onRun,
  showModel = true,
  showDiscovery = false,
  savingEnabled = false
}) => {
  if (!config) return null;
  return (
    <div className="agent-card">
      <div className="agent-card-header">
        <div>
          {stepLabel && <span className="agent-step-label">{stepLabel}</span>}
          <h3>{title}</h3>
          <p className="agent-card-desc">{description}</p>
          {outcomeNote && <p className="agent-card-outcome">{outcomeNote}</p>}
        </div>
        <label className="agent-switch" title={savingEnabled ? 'Saving…' : 'Schedule automatic runs'}>
          <input
            type="checkbox"
            checked={!!config.enabled}
            disabled={savingEnabled}
            onChange={(e) => onChange(configKey, { enabled: e.target.checked })}
          />
          <span className="agent-slider" />
        </label>
      </div>

      <div className="agent-card-grid">
        <label className="agent-field">
          <span>Run every (minutes)</span>
          <input
            type="number"
            min="5"
            value={config.intervalMinutes}
            onChange={(e) => onChange(configKey, { intervalMinutes: e.target.value })}
          />
        </label>

        {showModel && (
          <label className="agent-field">
            <span>Model</span>
            <select
              value={config.model}
              onChange={(e) => onChange(configKey, { model: e.target.value })}
            >
              {MODEL_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((model) => (
                    <option key={model.value} value={model.value}>{model.label}</option>
                  ))}
                </optgroup>
              ))}
              {config.model && !MODEL_VALUES.includes(config.model) && (
                <option value={config.model}>{config.model} (saved)</option>
              )}
            </select>
            <small className="agent-field-hint">
              This setting is only for this agent. Researcher and Menu agent each keep their own model.
            </small>
          </label>
        )}

        <label className="agent-field">
          <span>Max per run</span>
          <input
            type="number"
            min="1"
            value={config.maxPerRun}
            onChange={(e) => onChange(configKey, { maxPerRun: e.target.value })}
          />
        </label>
      </div>

      {showDiscovery && (
        <div className="agent-discovery">
          <label className="agent-field agent-field-full">
            <span>What to look for</span>
            <textarea
              rows={10}
              value={config.brief ?? ''}
              placeholder={DEFAULT_BRIEF_PLACEHOLDER}
              onChange={(e) => onChange(configKey, { brief: e.target.value })}
            />
            <small className="agent-field-hint">
              Goes straight into the search prompt. Describe the kind of recipes worth adding —
              be specific about what counts as a good result and what doesn't.
            </small>
          </label>

          <label className="agent-field agent-field-full">
            <span>Current focus (optional)</span>
            <input
              type="text"
              placeholder='e.g. "bbq", "fall soups", "dinners under 30 minutes"'
              value={config.theme || ''}
              onChange={(e) => onChange(configKey, { theme: e.target.value })}
            />
            <small className="agent-field-hint">
              A temporary narrowing layered on top of the brief. Leave blank for the full brief.
            </small>
          </label>

          <div className="agent-field agent-field-full">
            <span>Never return these</span>
            <div className="agent-chip-row">
              {EXCLUDABLE_CATEGORIES.map((category) => {
                const selected = (config.excludeCategories || []).includes(category);
                return (
                  <button
                    key={category}
                    type="button"
                    className={`agent-chip ${selected ? 'selected' : ''}`}
                    onClick={() => onChange(configKey, {
                      excludeCategories: selected
                        ? (config.excludeCategories || []).filter((value) => value !== category)
                        : [...(config.excludeCategories || []), category]
                    })}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
            <small className="agent-field-hint">
              Enforced in the prompt and again when screening results.
            </small>
          </div>

          <div className="agent-card-grid">
            <label className="agent-field">
              <span>Min ingredients</span>
              <input
                type="number"
                min="0"
                max="30"
                value={config.minIngredients ?? 0}
                onChange={(e) => onChange(configKey, { minIngredients: e.target.value })}
              />
              <small className="agent-field-hint">
                A rough proxy for a full meal over a single component. 0 disables it.
              </small>
            </label>

            <label className="agent-field agent-field-check">
              <input
                type="checkbox"
                checked={config.screenResults !== false}
                onChange={(e) => onChange(configKey, { screenResults: e.target.checked })}
              />
              <span>Screen results before queueing</span>
              <small className="agent-field-hint">
                One extra model call per run that re-checks each result against the brief.
                Costs far less than building a recipe you didn't want.
              </small>
            </label>
          </div>
        </div>
      )}

      <div className="agent-card-footer">
        <div className="agent-status">
          <span className={`agent-status-dot ${status?.isRunning ? 'running' : 'idle'}`} />
          {status?.isRunning ? 'Running...' : 'Idle'}
          {config.enabled && !status?.isRunning && (
            <span className="agent-status-meta agent-status-scheduled">Scheduled</span>
          )}
          {status?.lastRunAt && <span className="agent-status-meta">Last run: {formatDate(status.lastRunAt)}</span>}
          {status?.nextDueAt && config.enabled && (
            <span className="agent-status-meta">Next: {formatDate(status.nextDueAt)}</span>
          )}
        </div>
        {onRun && (
          <button
            className="agent-run-btn"
            disabled={status?.isRunning}
            onClick={() => onRun(configKey)}
          >
            Run now
          </button>
        )}
      </div>
    </div>
  );
};

const AdminAgents = () => {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [sources, setSources] = useState([]);
  const [queue, setQueue] = useState({ new: 0, blocked: 0 });
  const [reviewerLessons, setReviewerLessons] = useState([]);
  const [reasonLabels, setReasonLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearingQueue, setClearingQueue] = useState(null);
  const [savingEnabledKey, setSavingEnabledKey] = useState(null);
  const settingsRef = useRef(null);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  const persistSettings = useCallback(async (payload, { successMessage, updateSettings = true } = {}) => {
    const response = await fetch(`${apiBase()}/settings`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to save settings');
    }
    const data = await response.json();
    if (updateSettings) {
      setSettings(data.data.settings);
    }
    setStatus(data.data.status);
    if (successMessage) toast.success(successMessage);
    return data;
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [settingsRes, runsRes, sourcesRes, lessonsRes] = await Promise.all([
        fetch(`${apiBase()}/settings`, { headers: authHeaders() }),
        fetch(`${apiBase()}/runs`, { headers: authHeaders() }),
        fetch(`${apiBase()}/sources`, { headers: authHeaders() }),
        fetch(`${process.env.REACT_APP_API_URL}/admin/reviewer-lessons`, { headers: authHeaders() })
      ]);

      if (!settingsRes.ok) throw new Error('Failed to load agent settings');

      const settingsData = await settingsRes.json();
      setSettings(settingsData.data.settings);
      setStatus(settingsData.data.status);

      if (runsRes.ok) setRuns((await runsRes.json()).data || []);
      if (sourcesRes.ok) {
        const sourcesData = await sourcesRes.json();
        setSources(sourcesData.data || []);
        setQueue(sourcesData.queue || { new: 0, blocked: 0 });
      }
      if (lessonsRes.ok) {
        const lessonsData = await lessonsRes.json();
        setReviewerLessons(lessonsData.lessons || []);
        setReasonLabels(lessonsData.reasonLabels || {});
      }
    } catch (error) {
      console.error('Error loading agents data:', error);
      toast.error(error.message || 'Failed to load AI agents');
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll runs/status only — refreshing settings every 15s was resetting unsaved toggles.
  const refreshActivity = useCallback(async () => {
    try {
      const [settingsRes, runsRes, sourcesRes] = await Promise.all([
        fetch(`${apiBase()}/settings`, { headers: authHeaders() }),
        fetch(`${apiBase()}/runs`, { headers: authHeaders() }),
        fetch(`${apiBase()}/sources`, { headers: authHeaders() })
      ]);

      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setStatus(settingsData.data.status);
      }
      if (runsRes.ok) setRuns((await runsRes.json()).data || []);
      if (sourcesRes.ok) {
        const sourcesData = await sourcesRes.json();
        setSources(sourcesData.data || []);
        setQueue(sourcesData.queue || { new: 0, blocked: 0 });
      }
    } catch (error) {
      console.error('Error refreshing agent activity:', error);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const id = setInterval(refreshActivity, 15000);
    return () => clearInterval(id);
  }, [refreshActivity]);

  const handleConfigChange = async (configKey, partial) => {
    const current = settingsRef.current;
    if (!current) return;

    const nextSettings = {
      ...current,
      [configKey]: { ...current[configKey], ...partial }
    };
    setSettings(nextSettings);

    if (!Object.prototype.hasOwnProperty.call(partial, 'enabled')) return;

    const previousEnabled = current[configKey].enabled;
    const togglePayload =
      configKey === 'menuResearcher'
        ? { menuResearcher: { enabled: partial.enabled } }
        : { menuAgent: { enabled: partial.enabled } };

    setSavingEnabledKey(configKey);
    try {
      await persistSettings(togglePayload, {
        updateSettings: false,
        successMessage: partial.enabled
          ? 'Agent scheduled — will run on the interval below'
          : 'Automatic runs turned off (Run now still works)'
      });
    } catch (error) {
      console.error('Error saving agent toggle:', error);
      setSettings((prev) => ({
        ...prev,
        [configKey]: { ...prev[configKey], enabled: previousEnabled }
      }));
      toast.error(error.message || 'Failed to save schedule toggle');
    } finally {
      setSavingEnabledKey(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistSettings(
        {
          menuResearcher: settings.menuResearcher,
          menuAgent: settings.menuAgent
        },
        { successMessage: 'Agent settings saved' }
      );
    } catch (error) {
      console.error('Error saving agent settings:', error);
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleClearQueue = async (scope) => {
    const waiting = scope === 'all' ? queue.new : queue.blocked;
    const confirmMessage = scope === 'all'
      ? `Mark all ${waiting} NEW sources as FAILED? The menu agent will stop retrying them. The researcher will not re-queue the same URLs.`
      : `Retire ${waiting} NEW sources that already failed with 403/402? Fresh never-tried URLs stay in the queue.`;
    if (waiting <= 0) {
      toast.error(scope === 'all' ? 'The NEW queue is already empty.' : 'No blocked NEW sources to retire.');
      return;
    }
    if (!window.confirm(confirmMessage)) return;

    setClearingQueue(scope);
    try {
      const response = await fetch(`${apiBase()}/sources/clear`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ scope })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to clear the queue');
      toast.success(`Retired ${data.data?.retired ?? 0} source${data.data?.retired === 1 ? '' : 's'} from the queue`);
      await refreshActivity();
    } catch (error) {
      console.error('Error clearing source queue:', error);
      toast.error(error.message || 'Failed to clear the queue');
    } finally {
      setClearingQueue(null);
    }
  };

  const handleRun = async (configKey) => {
    const agent = configKey === 'menuResearcher' ? 'researcher' : 'menu';
    try {
      const response = await fetch(`${apiBase()}/run/${agent}`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start agent');
      toast.success(data.message || 'Agent started');
      setTimeout(refreshActivity, 1500);
    } catch (error) {
      console.error('Error starting agent:', error);
      toast.error(error.message || 'Failed to start agent');
    }
  };

  if (loading) {
    return <div className="agents-loading">Loading AI agents...</div>;
  }

  if (!settings) {
    return <div className="agents-loading">Unable to load AI agents.</div>;
  }

  return (
    <div className="admin-agents">
      <div className="agents-intro">
        <h2>AI Agents</h2>
        <p>
          Two-step pipeline: the <strong>Researcher</strong> only discovers recipe website URLs
          (saved in <strong>Discovered sources</strong> below). The <strong>Menu agent</strong> scrapes
          those URLs and creates <strong>PENDING</strong> menus for your approval under{' '}
          <strong>Menu Management</strong>. Neither agent publishes menus automatically.
        </p>
        <ol className="agents-pipeline">
          <li>
            <strong>Researcher</strong> — Gemini + search → new URLs in the queue (does not create recipes).
          </li>
          <li>
            <strong>Menu agent</strong> — scrapes <code>NEW</code> sources → draft menus for admin review.
          </li>
        </ol>
      </div>

      <div className="agents-grid">
        <AgentConfigCard
          title="AI Menu Researcher"
          stepLabel="Step 1 · Discovery"
          description="Uses Gemini + Google Search to find recipes matching the brief below, skipping dishes we already have."
          outcomeNote="Output: rows in Discovered sources (URL + title). No MenuItem recipes."
          configKey="menuResearcher"
          config={settings.menuResearcher}
          status={status?.researcher}
          onChange={handleConfigChange}
          onRun={handleRun}
          showDiscovery
          savingEnabled={savingEnabledKey === 'menuResearcher'}
        />

        <AgentConfigCard
          title="AI Menu Agent"
          stepLabel="Step 2 · Scrape & draft"
          description="Reads NEW discovered sources, extracts the recipe, and creates PENDING menus."
          outcomeNote="Output: PENDING menus in Menu Management for you to approve or reject."
          configKey="menuAgent"
          config={settings.menuAgent}
          status={status?.menu}
          onChange={handleConfigChange}
          onRun={handleRun}
          savingEnabled={savingEnabledKey === 'menuAgent'}
        />

        <div className="agent-card agent-card-disabled">
          <div className="agent-card-header">
            <div>
              <h3>AI Fitness Guru</h3>
              <p className="agent-card-desc">Pulls in exercises with YouTube demos. Coming soon.</p>
            </div>
            <span className="agent-coming-soon">Coming soon</span>
          </div>
        </div>
      </div>

      <div className="agents-save-bar">
        <p className="agents-save-hint">
          The schedule toggle saves immediately. After changing the model, interval, or max-per-run, click Save settings — the Menu agent model is stored separately from the Researcher.
        </p>
        <button className="agent-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save settings'}
        </button>
      </div>

      <div className="agents-section">
        <h3>Reviewer lessons</h3>
        <p className="agents-table-footnote" style={{ marginTop: 0 }}>
          Notes left from <strong>Menu Management</strong> when a reviewer hides or flags a recipe.
          The researcher reads the latest of these on each run so it can avoid the same class of dish.
        </p>
        <div className="agents-table-wrap">
          <table className="agents-table">
            <thead>
              <tr>
                <th>Recipe</th>
                <th>Reason</th>
                <th>Note</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {reviewerLessons.length === 0 && (
                <tr><td colSpan="4" className="agents-empty">No reviewer feedback yet. Hide a recipe from Menu Management and leave a note.</td></tr>
              )}
              {reviewerLessons.map((lesson, index) => (
                <tr key={`${lesson.title}-${lesson.createdAt || index}`}>
                  <td>{lesson.title}</td>
                  <td>{reasonLabels[lesson.reason] || lesson.reason}</td>
                  <td className="agents-reason">{lesson.note || '—'}</td>
                  <td>{formatDate(lesson.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="agents-section">
        <h3>Recent runs</h3>
        <div className="agents-table-wrap">
          <table className="agents-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Found</th>
                <th>Created</th>
                <th>Skipped</th>
                <th>Failed</th>
                <th>Started</th>
                <th>Trace</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><td colSpan="9" className="agents-empty">No runs yet.</td></tr>
              )}
              {runs.map((run) => (
                <tr key={run._id}>
                  <td>{run.agent === 'researcher' ? 'Researcher' : 'Menu'}</td>
                  <td>{run.trigger}</td>
                  <td><span className={`agents-badge ${run.status.toLowerCase()}`}>{run.status}</span></td>
                  <td>{run.stats?.found ?? 0}</td>
                  <td>{run.stats?.created ?? 0}</td>
                  <td>{run.stats?.skipped ?? 0}</td>
                  <td>{run.stats?.failed ?? 0}</td>
                  <td>{formatDate(run.startedAt)}</td>
                  <td>
                    {run.langfuseTraceUrl ? (
                      <a
                        className="agents-trace-link"
                        href={run.langfuseTraceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open trace
                      </a>
                    ) : (
                      <span className="agents-trace-missing">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="agents-table-footnote">
          <strong>Found</strong> = URLs or sources processed. <strong>Created</strong> for Researcher = new
          discovered URLs; for Menu = new PENDING recipes. <strong>Failed</strong> = scrape/extract errors.
          {' '}<strong>Open trace</strong> opens the Langfuse run (new runs get a direct link; older runs
          search by run id).
        </p>
      </div>

      <div className="agents-section">
        <div className="agents-section-header">
          <div>
            <h3>Discovered sources</h3>
            <p className="agents-queue-meta">
              {queue.new} waiting in NEW
              {queue.blocked > 0 ? ` · ${queue.blocked} already blocked (403/402)` : ''}
            </p>
          </div>
          <div className="agents-queue-actions">
            <button
              type="button"
              className="agent-queue-btn"
              disabled={clearingQueue !== null || status?.menu?.isRunning || queue.blocked <= 0}
              onClick={() => handleClearQueue('blocked')}
            >
              {clearingQueue === 'blocked' ? 'Retiring…' : 'Retire blocked'}
            </button>
            <button
              type="button"
              className="agent-queue-btn agent-queue-btn-danger"
              disabled={clearingQueue !== null || status?.menu?.isRunning || queue.new <= 0}
              onClick={() => handleClearQueue('all')}
            >
              {clearingQueue === 'all' ? 'Clearing…' : 'Clear NEW queue'}
            </button>
          </div>
        </div>
        <div className="agents-table-wrap">
          <table className="agents-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Domain</th>
                <th>Status</th>
                <th>Reason</th>
                <th>Fetch</th>
                <th>Discovered</th>
              </tr>
            </thead>
            <tbody>
              {sources.length === 0 && (
                <tr><td colSpan="6" className="agents-empty">No sources discovered yet.</td></tr>
              )}
              {sources.map((source) => (
                <tr key={source._id}>
                  <td>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.title || source.url}
                    </a>
                  </td>
                  <td>{source.domain}</td>
                  <td><span className={`agents-badge ${source.status.toLowerCase()}`}>{source.status}</span></td>
                  <td className="agents-reason">{source.reason}</td>
                  <td>{source.fetchMethod || source.error || '—'}</td>
                  <td>{formatDate(source.discoveredAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="agents-table-footnote">
          The menu agent only processes <strong>NEW</strong> sources. After this deploy, a 403 that
          still fails Jina and Gemini is marked <strong>FAILED</strong> and leaves the queue. Quota
          (402) can stay NEW for up to 3 attempts, then it is retired too.
          {' '}<strong>Retire blocked</strong> immediately fails the 403/402 leftovers.
          {' '}<strong>Clear NEW queue</strong> fails every waiting URL — the researcher will not
          add those same links again.
        </p>
      </div>
    </div>
  );
};

export default AdminAgents;
